/**
 * Pure utility helpers shared across house-management components.
 * No side-effects, no store access — just data transformations.
 */

import { load_image_from } from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';

/**
 * Resolve the full-size image URL for a device card avatar.
 * Returns `undefined` when the device has no image so the card falls back to a placeholder.
 *
 * @param device - The device to extract the image from
 */
export function getDeviceImage(device: Device): string | undefined {
	if (!device?.image) return undefined;
	const server = device?.localImage ? '' : device?.server;
	return load_image_from(device.image, server).replace('thumb_', '');
}

/**
 * Extract the list of ids whose value is `true` from a checkbox selection map.
 *
 * @param selected - `Record<deviceId, boolean>` produced by checkbox toggles
 * @returns Array of selected device ids
 */
export function getSelectedIds(selected: Record<string, boolean>): string[] {
	return Object.keys(selected).filter((id) => selected[id]);
}

/** Escape text for safe insertion into HTML (e.g. API `errors.message` in `{@html}`). */
export function escapeHtml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

const TRANSFER_PROFILE_PASSWORD_PREFIX_LEGACY = 'transfer-profile-password-';
const TRANSFER_PROFILE_PASSWORD_PREFIX_V2 = 'transfer-profile-password-v2-';

/**
 * Stable DOM id for the per-profile password field in transfer ownership flows.
 *
 * @param email   - Profile email (sanitized for id)
 * @param variant - `legacy` (default) or `v2` prefix so Legacy and V2 modals stay unique
 */
export function transferProfilePasswordInputId(
	email: string,
	variant: 'legacy' | 'v2' = 'legacy',
): string {
	const safe = email.replace(/[^a-zA-Z0-9_-]/g, '_');
	const prefix =
		variant === 'v2' ? TRANSFER_PROFILE_PASSWORD_PREFIX_V2 : TRANSFER_PROFILE_PASSWORD_PREFIX_LEGACY;
	return `${prefix}${safe}`;
}

/**
 * Build HTML for transfer failure hint: optional server message appended to translated body.
 */
export function buildFailureMessageHtml(body: string, serverMsg: string, serverLabel: string): string {
	if (!serverMsg) return body;
	return `${body}<p class="mt-s mb-0"><strong>${escapeHtml(serverLabel)}</strong> ${escapeHtml(serverMsg)}</p>`;
}

/**
 * Use a translated API error key when i18n returns a value different from the key; otherwise `fallback`.
 */
export function resolveTranslatedError(
	translatedValue: string | undefined,
	errorKey: string | undefined,
	fallback: string,
): string {
	if (!errorKey || translatedValue === undefined) return fallback;
	return translatedValue !== errorKey ? translatedValue : fallback;
}
